import dayjs, { Dayjs } from 'dayjs';
import { useLocalObservable } from 'mobx-react-lite';
import { ElementType } from 'react';
import { Navigate, ToolbarProps, View, Views } from 'react-big-calendar';

import { IcDay24Icon, IcMonth24Icon, IcWeek24Icon } from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';
import dateService from '@gem/services/date-service';

function formatWeekRange(date: Dayjs): string {
  const start = date.startOf('week');
  const end = date.endOf('week');

  const sameMonth = start.month() === end.month();
  const sameYear = start.year() === end.year();

  if (sameYear) {
    if (sameMonth) {
      return `${dateService.format(start, {
        day: 'numeric',
      })}–${dateService.format(end, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })}`;
    }
    return `${dateService.format(start, {
      day: 'numeric',
      month: 'long',
    })} – ${dateService.format(end, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })}`;
  }

  return `${dateService.format(start, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })} – ${dateService.format(end, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })}`;
}

interface IToolbarPresenterParams {
  onNavigate: ToolbarProps['onNavigate'];
  onView: ToolbarProps['onView'];
  onCreate(): void;
}

interface IToolbarPresenter {
  viewList: Array<{ key: string; title: string; icon: ElementType }>;
  onChangeDate(date: Date): void;
  onViewChange(view: View): void;
  onToday(): void;
  onPrev(): void;
  onNext(): void;
  onCreate(): void;
  getFormatTitle(date: Date, view: View): string;
}

export const useToolbarPresenter = (props: IToolbarPresenterParams): IToolbarPresenter => {
  const { t } = useTranslation();
  const { onNavigate, onView } = props;

  const presenter = useLocalObservable<IToolbarPresenter>(() => ({
    viewList: [
      { key: Views.DAY, title: t('calendar_view_day'), icon: IcDay24Icon },
      { key: Views.WEEK, title: t('calendar_view_week'), icon: IcWeek24Icon },
      { key: Views.MONTH, title: t('calendar_view_month'), icon: IcMonth24Icon },
    ],

    onViewChange(view: View): void {
      onView(view);
    },

    onChangeDate(date: Date): void {
      onNavigate(Navigate.DATE, date);
    },

    onToday(): void {
      onNavigate(Navigate.TODAY);
    },

    onPrev(): void {
      onNavigate(Navigate.PREVIOUS);
    },

    onNext(): void {
      onNavigate(Navigate.NEXT);
    },

    onCreate(): void {
      props.onCreate();
    },

    getFormatTitle(date: Date, view: View): string {
      switch (view) {
        case Views.DAY:
          return (
            dateService.format(date, {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            }) || ''
          );
        case Views.WEEK:
          return formatWeekRange(dayjs(date));
        case Views.MONTH:
          return (
            dateService.format(date, {
              month: 'long',
              year: 'numeric',
            }) || ''
          );
      }

      return '';
    },
  }));

  return presenter;
};
